import { TIER_DISPLAY_NAMES, TIER_MIN_STORAGE } from '@/atoms/subscription.atom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { BillingInterval, SubscriptionTier } from '@/graphql/graphql';
import { usePricing } from '@/hooks/usePricing';
import { useSubscription } from '@/hooks/useSubscription';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { analytics } from '@/lib/analytics';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

interface TierInfo {
  tier: SubscriptionTier;
  name: string;
  description: string;
  inherits?: string;
  features: string[];
  comingSoon?: boolean;
}

const TIER_ORDER: SubscriptionTier[] = ['LITE', 'CORE', 'PLUS', 'ENTERPRISE'];

interface TierSelectorProps {
  onTierSelect?: (tier: SubscriptionTier) => void;
  showChangeButton?: boolean;
}

export const TierSelector = ({ onTierSelect, showChangeButton = true }: TierSelectorProps) => {
  const { subscription, changeTier, loading } = useSubscription();
  const { getStartingPrice, formatPrice, isLoading: pricingLoading } = usePricing();
  const { t } = useTranslation('subscription');
  const hasScheduledTier = subscription.scheduledTier != null;
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);

  const effectiveTier = subscription.scheduledTier ?? subscription.tier;
  const currentTierIndex = TIER_ORDER.indexOf(subscription.tier);
  const billingInterval: BillingInterval = subscription.billingInterval || 'MONTHLY';
  const storageBlocks = subscription.scheduledStorageBlocks ?? subscription.storageBlocks;

  const TIER_INFO: TierInfo[] = [
    {
      tier: 'LITE',
      name: t('tiers.lite.name'),
      description: t('tiers.lite.description'),
      features: t('tiers.liteFeatures', { returnObjects: true }) as string[],
    },
    {
      tier: 'CORE',
      name: t('tiers.core.name'),
      description: t('tiers.core.description'),
      inherits: t('tiers.lite.name'),
      features: t('tiers.coreFeatures', { returnObjects: true }) as string[],
    },
    {
      tier: 'PLUS',
      name: t('tiers.plus.name'),
      description: t('tiers.plus.description'),
      inherits: t('tiers.core.name'),
      comingSoon: true,
      features: t('tiers.plusFeatures', { returnObjects: true }) as string[],
    },
    {
      tier: 'ENTERPRISE',
      name: t('tiers.enterprise.name'),
      description: t('tiers.enterprise.description'),
      inherits: t('tiers.plus.name'),
      comingSoon: true,
      features: t('tiers.enterpriseFeatures', { returnObjects: true }) as string[],
    },
  ];

  const handleTierSelect = (tier: SubscriptionTier) => {
    setSelectedTier(tier);
    onTierSelect?.(tier);
  };

  const handleChangeTier = async () => {
    if (!selectedTier || selectedTier === subscription.tier) return;

    const selectedIndex = TIER_ORDER.indexOf(selectedTier);
    const isUpgrade = selectedIndex > currentTierIndex;

    const id = toast.loading(
      isUpgrade
        ? t('tierSelector.upgrading', { tier: TIER_DISPLAY_NAMES[selectedTier] })
        : t('tierSelector.schedulingDowngrade', { tier: TIER_DISPLAY_NAMES[selectedTier] }),
    );

    try {
      await changeTier(selectedTier);
      analytics.capture('Subscription Tier Changed', { tier: selectedTier, action: isUpgrade ? 'upgrade' : 'downgrade' });
      toast.success(
        isUpgrade
          ? t('tierSelector.upgradedSuccess', { tier: TIER_DISPLAY_NAMES[selectedTier] })
          : t('tierSelector.downgradeScheduled', { tier: TIER_DISPLAY_NAMES[selectedTier] }),
        { id },
      );
      setSelectedTier(null);
    } catch (err) {
      toast.error(t('tierSelector.failedChangeTier'), { id });
    }
  };

  const getTierAction = (tier: SubscriptionTier) => {
    const tierIndex = TIER_ORDER.indexOf(tier);

    if (tier === subscription.tier) {
      return { label: t('tierSelector.currentPlan'), disabled: true, variant: 'outline' as const };
    }
    if (hasScheduledTier && tier === subscription.scheduledTier) {
      return { label: t('tierSelector.scheduled'), disabled: true, variant: 'outline' as const };
    }
    if (tierIndex > currentTierIndex) {
      return { label: t('tierSelector.upgrade'), disabled: false, variant: 'default' as const };
    }
    if (tier === 'LITE' && storageBlocks > 1) {
      return { label: t('tierSelector.reduceStorageFirst'), disabled: true, variant: 'secondary' as const };
    }
    return { label: t('tierSelector.downgrade'), disabled: false, variant: 'secondary' as const };
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('tierSelector.title')}</CardTitle>
        <CardDescription>
          {t('tierSelector.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className='grid grid-cols-2 gap-3'>
          {TIER_INFO.map((tierInfo) => {
            const action = getTierAction(tierInfo.tier);
            const isSelected = selectedTier === tierInfo.tier;
            const isCurrent = tierInfo.tier === subscription.tier;
            const isScheduled = hasScheduledTier && tierInfo.tier === subscription.scheduledTier;
            const isComingSoon = tierInfo.comingSoon === true;

            return (
              <div
                key={tierInfo.tier}
                className={`relative rounded-lg border p-3 transition-all ${
                  isComingSoon
                    ? 'opacity-60 cursor-not-allowed border-slate-200 dark:border-stone-800'
                    : isSelected
                      ? 'border-blue-500 ring-2 ring-blue-500 cursor-pointer'
                      : isScheduled
                        ? 'border-blue-400/60 bg-blue-50/50 dark:bg-blue-950/10 cursor-pointer'
                        : isCurrent
                          ? 'border-green-500/60 bg-green-50/50 dark:bg-green-950/10 cursor-pointer'
                          : 'border-slate-200 dark:border-stone-800 hover:border-slate-300 dark:hover:border-stone-700 cursor-pointer'
                }`}
                onClick={() => !action.disabled && !isComingSoon && handleTierSelect(tierInfo.tier)}>
                {isComingSoon && (
                  <Badge
                    className='absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] px-1.5 py-0'
                    variant='secondary'>
                    {t('tierSelector.comingSoon')}
                  </Badge>
                )}
                {!isComingSoon && tierInfo.tier === 'CORE' && currentTierIndex < TIER_ORDER.indexOf('CORE') && (
                  <Badge
                    className='absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] px-1.5 py-0'
                    variant='default'>
                    {t('tierSelector.recommended')}
                  </Badge>
                )}

                <div className='flex flex-col h-full gap-2'>
                  <div>
                    <h3 className='text-sm font-semibold text-slate-900 dark:text-white'>{tierInfo.name}</h3>
                    <p className='text-xs text-slate-500 dark:text-stone-400'>{tierInfo.description}</p>
                  </div>

                  {/* Pricing */}
                  <div>
                    {pricingLoading ? (
                      <div className='h-6 w-16 bg-slate-200 dark:bg-stone-800 rounded animate-pulse' />
                    ) : (
                      <div className='flex items-baseline gap-1'>
                        <span className='text-lg font-bold text-slate-900 dark:text-white'>
                          {formatPrice(getStartingPrice(tierInfo.tier, billingInterval, storageBlocks))}
                        </span>
                        <span className='text-xs text-slate-400 dark:text-stone-500'>
                          /100GB/{billingInterval === 'MONTHLY' ? 'mo' : 'yr'}
                        </span>
                      </div>
                    )}
                  </div>

                  {tierInfo.inherits && (
                    <p className='text-[11px] text-slate-400 dark:text-stone-500 font-medium'>
                      {t('tierSelector.everythingIn', { tier: tierInfo.inherits })}
                    </p>
                  )}

                  <ul className='space-y-1'>
                    {tierInfo.features.map((feature, index) => (
                      <li key={index} className='flex items-center gap-1.5 text-xs text-slate-600 dark:text-stone-300'>
                        <Check className='h-3 w-3 shrink-0 text-green-500' />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {showChangeButton &&
                    (isComingSoon ? (
                      <Button variant='outline' size='sm' className='w-full mt-auto h-7 text-xs' disabled>
                        {t('tierSelector.comingSoon')}
                      </Button>
                    ) : (
                      <Button
                        variant={action.variant}
                        size='sm'
                        className='w-full mt-auto h-7 text-xs'
                        disabled={action.disabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTierSelect(tierInfo.tier);
                        }}>
                        {action.label}
                      </Button>
                    ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Scheduled change banner */}
        {hasScheduledTier && (
          <div className='mt-4 flex items-center justify-between rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 p-3'>
            <div>
              <p className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                {t('tierSelector.downgradeToScheduled', { tier: TIER_DISPLAY_NAMES[subscription.scheduledTier as SubscriptionTier] })}
              </p>
              <p className='text-xs text-blue-600 dark:text-blue-400'>{t('tierSelector.takesEffectAtEnd')}</p>
            </div>
            <Button
              variant='outline'
              size='sm'
              className='shrink-0 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900/30'
              disabled={loading}
              onClick={async () => {
                const id = toast.loading(t('tierSelector.cancellingDowngrade'));
                try {
                  await changeTier(subscription.tier);
                  toast.success(t('tierSelector.downgradeCancelled'), { id });
                } catch {
                  toast.error(t('tierSelector.failedCancelChange'), { id });
                }
              }}>
              {loading ? t('tierSelector.processing') : t('tierSelector.cancelDowngrade')}
            </Button>
          </div>
        )}

        {/* Tier change confirmation dialog (upgrade & downgrade) */}
        {(() => {
          const isDialogOpen = showChangeButton && !!selectedTier && selectedTier !== subscription.tier;
          const isUpgrading = isDialogOpen && TIER_ORDER.indexOf(selectedTier!) > currentTierIndex;
          const storageWillChange =
            isDialogOpen && selectedTier && TIER_MIN_STORAGE[selectedTier] > subscription.storageBlocks;

          return (
            <AlertDialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                if (!open) setSelectedTier(null);
              }}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {isUpgrading ? t('tierSelector.upgradeTo', { tier: selectedTier ? TIER_DISPLAY_NAMES[selectedTier] : '' }) : t('tierSelector.downgradeTo', { tier: selectedTier ? TIER_DISPLAY_NAMES[selectedTier] : '' })}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {isUpgrading
                      ? t('tierSelector.upgradeAppliedImmediately')
                      : t('tierSelector.downgradeTakesEffect')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {storageWillChange && selectedTier && (
                  <div className='rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 px-3 py-2'>
                    <p className='text-sm text-blue-800 dark:text-blue-200'>
                      {t('tierSelector.storageWillIncrease', { from: subscription.storageBlocks * 100, to: TIER_MIN_STORAGE[selectedTier] * 100, tier: TIER_DISPLAY_NAMES[selectedTier] })}
                    </p>
                  </div>
                )}
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('tierSelector.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleChangeTier} disabled={loading}>
                    {loading ? t('tierSelector.processing') : isUpgrading ? t('tierSelector.confirmUpgrade') : t('tierSelector.confirmDowngrade')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          );
        })()}
      </CardContent>
    </Card>
  );
};
